// Analysis.js
import { authHeader } from '../utils/authHeaders';
import React, { useEffect, useState, useRef, useCallback } from 'react'
import ChatInput from '../components/sub-components/ChatInput'
import Sidebar from '../components/sub-components/Sidebar'
import constants, { BASE_QUERYURL } from '../components/constants'
import { useThemeColor } from '../hooks/useThemeColor'
import { useOrganizationLogo } from '../hooks/useOrganizationLogo'
import ChatBlob from '../components/atomic/ChatBlob'
import { useParams, useNavigate } from 'react-router-dom'
import { Home, ChevronDown, Share2, Shield, Lock, Info, AlertTriangle, Eye, Building2, X } from 'lucide-react'
import ShareModal from '../components/ShareModal'
import { deleteHistory } from '../Api/req'

import { fetchAIProfileByTaskAsync, selectAIProfile } from '../slice/aiProfileSlice';
import { fetchAgentLayout } from '../slice/agentWidgetsSlice';
import { useSelector, useDispatch } from 'react-redux'
import { setHistoryId } from '../slice/storage'
import { fetchChatHistory } from '../slice/api/historySlice'
import { fetchQAHistory } from '../slice/qaHistorySlice'
import { selectSQ } from '../slice/sqSlice'
import { getTaskStatusesByHistoryAsync } from '../slice/taskStatusSlice'
import { clearChatHistory, sendChatMessage, fetchRunningAnalysisJob, resumeAnalysisJob } from '../slice/chatSlice'
import { resetPdfUrl } from '../slice/pdfViewslice'
import { clearSources } from '../slice/sourceSlice'
import { renderToString } from 'react-dom/server';
import markdownStyles from '../components/markdown-styles.module.css'

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'
import { useToast } from '../components/Toast'
import { useTranslation } from '../i18n/LanguageContext'
import ChatZoomControls from '../components/sub-components/ChatZoomControls'
import { useChatZoom } from '../hooks/useChatZoom'

// Import the app styles
// eslint-disable-next-line import/no-webpack-loader-syntax
import appStyle from '!!css-loader?{"sourceMap":false,"exportType":"string"}!../App.css';

// Matches the icon keys saved from Agent Settings > General Settings' Privacy
// disclaimer icon picker (Overview.jsx's DISCLAIMER_ICONS) — kept as a small
// standalone map here since this file only needs lookup-by-key, not the
// iterable list Overview.jsx's picker UI needs.
const DISCLAIMER_ICON_MAP = { shield: Shield, lock: Lock, info: Info, warning: AlertTriangle, eye: Eye, building: Building2 };

// All 5 steps, in the order Azure's own real events actually fire them —
// driven by real backend state (see /query/search-status/{historyId}), not a
// fake timer cycling through these regardless of what's actually happening.
// "checking" is the one step with real dynamic content: once Azure reveals
// the actual search query it ran, that replaces the plain label.
const STEP_LABEL_BY_STEP = {
  understanding: 'chatActions.webSearchStep1',
  searching: 'chatActions.webSearchStep2',
  reviewing: 'chatActions.webSearchStep3',
  checking: 'chatActions.webSearchStep4',
  generating: 'chatActions.webSearchStep5',
};

// Polls Azure's real, live progress (GET /query/search-status/{historyId})
// while a web/hybrid answer is being generated, to know which of the 5 steps
// to show — not a fake, time-based animation cycling through labels
// regardless of what's actually happening.
// `active` is whether THIS chat's request is currently loading (parent
// passes `loading && loadingHistoryId === historyId`). The component stays
// mounted continuously (parent never unmounts it based on `active` alone) so
// that when the real answer arrives and `active` flips to false, any steps
// that arrived in a last-moment burst and haven't finished their on-screen
// time yet are NOT cut off — it keeps draining its queue and self-hides only
// once there's genuinely nothing left to show.
const WebSearchLoadingIndicator = ({ color, wide, historyId, active, onProgressChange }) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(null);
  const [draining, setDraining] = useState(false);
  // Live reasoning-summary text (Azure's reasoning.summary, "Show Reasoning"
  // toggle) — only ever set when the admin turned this on and the model is
  // actually producing one; always the FULL text so far, so it's rendered
  // directly rather than run through the discrete-step queue/drain above.
  const [reasoningText, setReasoningText] = useState('');
  // How many steps we've already fetched (passed back as `?after=` so the
  // backend only sends new ones) and any fetched-but-not-yet-shown steps.
  const afterRef = useRef(0);
  const queueRef = useRef([]);
  const drainTimerRef = useRef(null);
  const unmountedRef = useRef(false);
  const wasActiveRef = useRef(false);

  useEffect(() => () => { unmountedRef.current = true; }, []);

  // Azure often fires several real transitions (searching -> reviewing ->
  // checking -> generating) faster than the 400ms poll interval, so one poll
  // can return multiple new steps at once. Showing only the latest would
  // skip whichever ones happened in between — this displays each queued step
  // for a minimum visible duration before moving to the next, so every label
  // the backend recorded actually gets seen. Only checks unmountedRef (true
  // component unmount), not the polling effect's own start/stop, so a burst
  // that lands right as `active` flips to false still finishes draining.
  const drainQueue = useCallback(() => {
    if (unmountedRef.current || drainTimerRef.current) return;
    const next = queueRef.current.shift();
    if (!next) {
      setDraining(false);
      return;
    }
    setProgress(next);
    drainTimerRef.current = setTimeout(() => {
      drainTimerRef.current = null;
      drainQueue();
    }, 350);
  }, []);

  // Poll only while active; stopping the interval on cleanup does NOT touch
  // the drain timer above, so an in-progress display sequence survives past
  // `active` turning false instead of being abruptly cut short.
  useEffect(() => {
    if (!active || !historyId) {
      wasActiveRef.current = active;
      return;
    }
    // A fresh request just started — either a different chat (historyId
    // changed) or a second question in the SAME chat (active went
    // false -> true again). The backend hard-resets its step list at the
    // start of every request even when history_id is reused, so our own
    // `after` index must reset too — otherwise it stays ahead of the
    // backend's freshly-shorter list and none of the new request's steps
    // ever satisfy `count > after`, so nothing gets queued/shown.
    if (!wasActiveRef.current) {
      afterRef.current = 0;
      queueRef.current = [];
      if (drainTimerRef.current) {
        clearTimeout(drainTimerRef.current);
        drainTimerRef.current = null;
      }
      setProgress({ step: 'understanding', query: null });
      setDraining(false);
      setReasoningText('');
    }
    wasActiveRef.current = true;

    let cancelled = false;
    const poll = () => {
      fetch(`${BASE_QUERYURL}/search-status/${historyId}?after=${afterRef.current}`, {
        headers: { ...authHeader() },
      })
        .then((res) => res.json())
        .then((data) => {
          if (cancelled || unmountedRef.current || !data || !Array.isArray(data.steps)) return;
          if (data.count > afterRef.current) {
            queueRef.current.push(...data.steps);
            afterRef.current = data.count;
            setDraining(true);
            drainQueue();
          }
          if (typeof data.reasoning_text === 'string' && data.reasoning_text) {
            setReasoningText(data.reasoning_text);
          }
        })
        .catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 400);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [historyId, active, drainQueue]);

  // This indicator's own appearance/step-text changes grow the chat
  // container's content independently of chatHistory/qaHistory — the
  // parent's scroll-to-bottom effect only watches those two, so without
  // this it never learns the indicator just showed up or changed text.
  useEffect(() => {
    onProgressChange?.();
  }, [progress, reasoningText, draining, onProgressChange]);

  if (!active && !draining) return null;
  if (!progress) return null;

  // "checking" is when Azure has just revealed the real query it searched
  // for — show that instead of the plain step label. Every other step stays
  // a plain label since no more specific real data exists at that point.
  const label = progress.step === 'checking' && progress.query
    ? t('chatActions.webSearchFoundQuery', { query: progress.query })
    : t(STEP_LABEL_BY_STEP[progress.step] || STEP_LABEL_BY_STEP.understanding);

  return (
    <div className={`${wide ? 'w-full' : 'max-w-3xl'}`}>
      {reasoningText && (
        <div
          className="mb-2 px-4 py-3 rounded-lg text-[13px] italic text-[#6B7280] overflow-y-auto"
          style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', maxHeight: '120px', whiteSpace: 'pre-wrap' }}
        >
          <span className="font-medium not-italic text-[#374151]">{t('chatActions.aiThinkingLabel')}</span> {reasoningText}
        </div>
      )}
      <div className="flex items-center gap-3 py-4">
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M9.83705 9.63783L11.383 5H12.3317L13.8776 9.63783L18.5154 11.1838V12.1325L13.8776 13.6784L12.3317 18.3162H11.383L9.83705 13.6784L5.19922 12.1325V11.1838L9.83705 9.63783Z" fill="white" />
          </svg>
        </div>
        <span className="text-[14px] text-[#374151]">{label}</span>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: color, animationDelay: '0ms' }}></div>
          <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: color, opacity: 0.6, animationDelay: '200ms' }}></div>
          <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: color, opacity: 0.3, animationDelay: '400ms' }}></div>
        </div>
      </div>
    </div>
  );
};

const RUNNING_JOB_RECHECK_WAITS_MS = [3000, 3000, 4000];

const Analysis = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userId = localStorage.getItem('id');
  const { primaryColor } = useThemeColor();
  const { logoUrl} = useOrganizationLogo();
  const { ToastContainer } = useToast();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const sidebarAutoAppliedRef = React.useRef(false)
  const [sidebarWidth, setSidebarWidth] = useState(445);
  const [description, setDescription] = useState();
  const [activeTab] = useState('questions'); // 'questions' or 'files'
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const exportDropdownRef = useRef(null);
  // Initialise from the persisted Nav collapsed state so navigating directly
  // into Analysis (after the user collapsed the sidebar elsewhere) doesn't
  // leave a 208px empty gap on the left.
  const [navWidth, setNavWidth] = useState(() => {
    try { return localStorage.getItem('navCollapsed') === '1' ? 72 : 280; } catch { return 280; }
  });

  // Listen for left Nav sidebar toggle
  useEffect(() => {
    const handleNavToggle = (event) => {
      setNavWidth(event.detail.isCollapsed ? 72 : 280);
    };
    window.addEventListener('sidebarToggle', handleNavToggle);
    return () => window.removeEventListener('sidebarToggle', handleNavToggle);
  }, []);

  // Load sidebar width from localStorage on component mount
  useEffect(() => {
    const savedWidth = localStorage.getItem('sidebarWidth');
    if (savedWidth) {
      const width = parseInt(savedWidth, 10);
      if (width >= 300 && width <= 800) {  // Validate width range
        setSidebarWidth(width);
      }
    }
  }, []);
  
  // Save width to localStorage when it changes
  const handleWidthChange = (newWidth) => {
    setSidebarWidth(newWidth);
    localStorage.setItem('sidebarWidth', newWidth.toString());
  };
  const [showScrollButton, setShowScrollButton] = useState(false);
  const chatContainerRef = useRef(null);
  const chatZoom = useChatZoom(chatContainerRef);
  const { chatHistory, loading, loadingHistoryId, analyzingImageHistoryId } = useSelector((state) => state.chat);
  const liveMessageCountRef = useRef(0);
  liveMessageCountRef.current = chatHistory?.length || 0;
  const chatRecordRef = useRef(null);
  const openSidebar = () => setIsSidebarOpen(true)
  const closeSidebar = () => setIsSidebarOpen(false)
  const taskID = useSelector((state) => state.dropdown.id);
  const { showProjectDetailByTask, overviewEnabledByTask, showFilesTabByTask, allowLocalFilesByTask, layoutLoadingByTask } = useSelector((state) => state.agentWidgets);
  const { tasks } = useSelector((state) => state.tasks);
  const aiProfile = useSelector(selectAIProfile);
  const dispatch = useDispatch();
  const chatHistoryData = useSelector((state) => state.history.chatHistory.history_records);
  chatRecordRef.current = chatHistoryData;
  const { historyId } = useParams();
  // Guard against a stale chatHistoryData still holding the PREVIOUS chat's
  // task_id right after switching chats (same pattern already used a few
  // lines down for the Sidebar's taskID prop) — without this, currentTask
  // can briefly (or, if the refetch stalls, persistently) resolve to the
  // wrong agent, showing that agent's disclaimer instead of the one actually
  // being viewed.
  const currentTaskId = chatHistoryData?.id === historyId ? chatHistoryData?.task_id : null;
  const currentTask = (tasks || []).find(
    (tk) => tk.id?.toString() === currentTaskId?.toString()
  );
  const isDirectChatTask = !!currentTask?.direct_chat_mode;
  // Only pure "Web only" mode hides the document sidebar/upload — "Documents + Web"
  // (hybrid) still needs the Files tab and uploads for its RAG half to work.
  // Falls back to the legacy boolean for any AIProfile saved before knowledge_source existed.
  const isPureWebOnly = aiProfile?.knowledge_source
    ? aiProfile.knowledge_source === 'web'
    : !!aiProfile?.web_search_enabled;
  const { userFiles, generatedDocuments } = useSelector((state) => state.api);
  // Web-only hides Files/Instructions entirely (no RAG upload), but once this
  // agent has generated a Word document there's something worth showing.
  const showGeneratedFilesOnly = isPureWebOnly && (generatedDocuments?.length > 0);
  const { qaHistory, loading: qaLoading } = useSelector((state) => state.qaHistory);
  const sidebarQuestions = useSelector(selectSQ);

  // Word hint (ChatBlob) shows only under this message, and clicking it turns
  // Word mode on + focuses ChatInput — the trigger is a counter, not a bool,
  // so the effect in ChatInput fires on every click even if already toggled on.
  const lastBotMessageId = React.useMemo(() => {
    const source = (chatHistory && chatHistory.length > 0) ? chatHistory : qaHistory;
    if (!source) return null;
    for (let i = source.length - 1; i >= 0; i--) {
      if (source[i].role !== 'user') return source[i].id;
    }
    return null;
  }, [chatHistory, qaHistory]);
  const [wordModeActivationSignal, setWordModeActivationSignal] = useState(0);
  const handleActivateWordMode = () => setWordModeActivationSignal((n) => n + 1);
  const [analysisModeActivationSignal, setAnalysisModeActivationSignal] = useState(0);
  const handleActivateDataAnalysisMode = () => setAnalysisModeActivationSignal((n) => n + 1);

  // Map sidebar-question title (case-insensitive, trimmed) -> sq_id for linking AI answers to their SQ
  const titleToSqId = React.useMemo(() => {
    const map = {};
    (sidebarQuestions || []).forEach(q => {
      if (q?.title) {
        map[String(q.title).toLowerCase().trim()] = q.id;
      }
    });
    return map;
  }, [sidebarQuestions]);
  const [setShowSearchSection] = useState(true)
  const toggleSearchSection = () => setShowSearchSection(prev => !prev)

  // Privacy disclaimer — one shared config per agent (Agent Settings >
  // General Settings), valid across every Knowledge Source mode. Dismissal
  // lives in plain component state, not localStorage/DB, on purpose: "closes
  // for the current visit, reappears on reopen" (see Overview.jsx's own info
  // notice) means it must NOT survive a remount — resetting whenever
  // historyId changes below achieves exactly that.
  const [disclaimerDismissed, setDisclaimerDismissed] = useState(false);
  const [showDisclaimerLearnMore, setShowDisclaimerLearnMore] = useState(false);
  useEffect(() => {
    setDisclaimerDismissed(false);
    setShowDisclaimerLearnMore(false);
  }, [historyId]);

  // Direct Chat Mode sessions are created the moment an agent is clicked, before
  // the user asks anything — if they leave without ever sending a message, the
  // empty placeholder session should be deleted instead of cluttering History.
  // Detection uses history_name still equalling the placeholder (set server-side
  // at creation, cleared server-side the moment a first question is auto-named —
  // see ChatInput.jsx) rather than a live message count: the placeholder check
  // fails *safe* if chatHistoryData hasn't finished loading yet (defaults to not
  // deleting), whereas a message count would fail *dangerous* — an unloaded real
  // session with actual messages would look like 0 and get wrongly deleted.
  const deleteCandidateRef = useRef(null);
  useEffect(() => {
    const tid = chatHistoryData?.task_id?.toString();
    const isDirectChat = !!(tasks || []).find((tk) => tk.id?.toString() === tid)?.direct_chat_mode;
    const isStillPlaceholder = chatHistoryData?.id === historyId
      && chatHistoryData?.history_name === t('homepage.newChatPlaceholderName');
    deleteCandidateRef.current = (isDirectChat && isStillPlaceholder) ? historyId : null;
  }, [chatHistoryData, tasks, historyId, t]);
  useEffect(() => {
    const idToClean = historyId;
    return () => {
      if (deleteCandidateRef.current && deleteCandidateRef.current === idToClean) {
        deleteHistory(idToClean).catch(() => {});
      }
    };
  }, [historyId]);

  const globalCommentsRef = useRef({});

  // Comment threads live in their own table; pull them fresh at export time.
  const loadCommentsForExport = async () => {
    if (!historyId) return;
    try {
      const res = await fetch(
        `${BASE_QUERYURL}/answer-comments/by-history?history_id=${encodeURIComponent(historyId)}`,
        { headers: { ...authHeader() } }
      );
      if (!res.ok) return;
      const data = await res.json();
      const map = {};
      Object.entries(data?.comments_by_question || {}).forEach(([questionId, list]) => {
        const text = (list || [])
          .map((c) => `${c.author_name || c.author_email || t('share.commentsUnknownAuthor')}: ${c.comment_text}`)
          .join('\n');
        if (text.trim()) map[questionId] = text;
      });
      globalCommentsRef.current = map;
    } catch {
      // export still proceeds with whatever the other sources hold
    }
  };

  console.log(qaHistory, 'qa');

  useEffect(() => {
    dispatch(clearChatHistory());
  }, [dispatch]);

  useEffect(() => {
    if (historyId && userId) {
      dispatch(fetchChatHistory({ historyId, userId }));
    }
    // Cleanup function when component unmounts or historyId changes
    return () => {
      dispatch(clearChatHistory());
      dispatch(resetPdfUrl());
      dispatch(clearSources());
    };
  }, [dispatch, historyId, userId]);

  useEffect(() => {
    if (!chatHistoryData?.task_id || chatHistoryData.id !== historyId) return undefined;
    let cancelled = false;
    const qaArgs = { historyId, userId, taskId: chatHistoryData.task_id };
    dispatch(fetchAIProfileByTaskAsync(chatHistoryData.task_id));
    const findRunningJob = () => fetchRunningAnalysisJob(historyId).catch(() => undefined);
    dispatch(fetchQAHistory(qaArgs)).then(async (action) => {
      if (cancelled || !historyId || action.error) return;
      const qa = action.payload || [];
      const unanswered = !qa.length || qa[qa.length - 1].role === 'user';
      let job = await findRunningJob();
      const record = chatRecordRef.current;
      const usesAnalysis = !qa.length || (record?.id === historyId && Boolean(record.analysis_mode_on));
      if (job === null && unanswered && usesAnalysis) {
        for (const wait of RUNNING_JOB_RECHECK_WAITS_MS) {
          await new Promise((resolve) => setTimeout(resolve, wait));
          if (cancelled) return;
          job = await findRunningJob();
          if (job !== null) break;
        }
      }
      if (cancelled || job === undefined) return;
      if (job) {
        const questionMissing = qa[qa.length - 1]?.role !== 'user' && liveMessageCountRef.current === 0;
        dispatch(resumeAnalysisJob(job));
        if (questionMissing) dispatch(fetchQAHistory(qaArgs));
      } else if (usesAnalysis && qa.length && unanswered && liveMessageCountRef.current === 0) {
        dispatch(fetchQAHistory(qaArgs));
      }
    });
    return () => { cancelled = true; };
  }, [dispatch, chatHistoryData?.task_id, chatHistoryData?.id, historyId, userId]);

  useEffect(() => {
    if (historyId) {
      dispatch(getTaskStatusesByHistoryAsync(historyId));
    }
  }, [dispatch, historyId]);

  // Fetch agent layout first so we know if questions are enabled before showing the tab
  useEffect(() => {
    if (chatHistoryData?.task_id) {
      dispatch(fetchAgentLayout(chatHistoryData.task_id.toString()));
    }
  }, [dispatch, chatHistoryData?.task_id]);

  // SQ catalog is fetched exclusively by the Sidebar component to avoid double dispatch

  // Derive primitive flags for sidebar auto-collapse (primitives in deps = no render loop)
  const _tid = chatHistoryData?.task_id?.toString();
  const _questionsFlag = _tid !== undefined ? showProjectDetailByTask[_tid] : undefined;
  const _filesFlag     = _tid !== undefined ? showFilesTabByTask[_tid]       : undefined;
  const _flagsLoaded   = _questionsFlag !== undefined && _filesFlag !== undefined;
  const _bothOff       = _questionsFlag === false && _filesFlag === false;

  // Reset on project change (historyId) — ensures fresh state for every project
  useEffect(() => {
    setIsSidebarOpen(true);
    sidebarAutoAppliedRef.current = false;
  }, [historyId]);

  // Auto-collapse when flags confirm both are off; only fires once per project
  useEffect(() => {
    if (!_flagsLoaded || sidebarAutoAppliedRef.current) return;
    sidebarAutoAppliedRef.current = true;
    if (_bothOff) setIsSidebarOpen(false);
  }, [_flagsLoaded, _bothOff]);

  // Reset state on page leave (component unmount)
  useEffect(() => {
    return () => {
      sidebarAutoAppliedRef.current = false;
    };
  }, []);

  // File fetching is handled ONLY by Sidebar component to prevent duplicate fetches

  useEffect(() => {
    dispatch(setHistoryId(historyId));
  }, [historyId, dispatch]);

  const name = localStorage.getItem('name')

  // Stable reference (useCallback) so passing this to WebSearchLoadingIndicator
  // as onProgressChange doesn't re-trigger its effect on every unrelated
  // Analysis re-render.
  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, qaHistory, scrollToBottom]);

  // Same fix as WebSearchLoadingIndicator's own onProgressChange, for a
  // DIFFERENT thing that also grows the chat container's height without
  // changing chatHistory/qaHistory: the "Analyzing image…" indicator
  // appearing/disappearing (see ChatInput.jsx's waitForImagesToProcess).
  // Without this, it renders below the fold and the user has to scroll
  // down manually to even see it started, exactly like the earlier bug.
  useEffect(() => {
    scrollToBottom();
  }, [analyzingImageHistoryId, scrollToBottom]);

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 100;
    setShowScrollButton(!bottom);
};

  


  const exportChatsAsPDF = async (includeComments = true) => {
    if (includeComments) await loadCommentsForExport();

    // Function to get the latest comments from all possible sources
    const getLatestComments = () => {
      const commentMap = {};
      
      // 1. First priority: Use comments from our global comments ref
      // This contains the most up-to-date comments from all ChatBlob components
      if (globalCommentsRef.current) {
        Object.entries(globalCommentsRef.current).forEach(([messageId, commentText]) => {
          if (messageId && commentText && commentText.trim() !== '') {
            commentMap[messageId] = commentText;
          }
        });
      }
      
      // 2. Second priority: Find comments in the DOM
      document.querySelectorAll('[data-comment-for]').forEach(commentEl => {
        const messageId = commentEl.getAttribute('data-comment-for');
        const commentText = commentEl.textContent;
        if (messageId && commentText && commentText.trim() !== '' && !commentMap[messageId]) {
          commentMap[messageId] = commentText;
        }
      });
      
      // 3. Third priority: Extract comments from chat history state
      // Only add comments that aren't already in our map
      qaHistory.forEach(message => {
        if (message.id && message.comment && message.comment.trim() !== '' && !commentMap[message.id]) {
          commentMap[message.id] = message.comment;
        }
      });
      
      chatHistory.forEach(message => {
        const messageId = message.id || (message.content && message.content.id);
        if (messageId && message.comment && message.comment.trim() !== '' && !commentMap[messageId]) {
          commentMap[messageId] = message.comment;
        }
      });
      
      return commentMap;
    };
    
    // Get the latest comments from all sources
    const latestComments = getLatestComments();
    console.log('Latest comments for PDF export:', latestComments);
    
    // Create the merged history with the latest comments
    const mergedHistoryForPDF = [...qaHistory, ...chatHistory].map(message => {
      // Get the message ID - ensure we're getting the correct ID
      let messageId;
      let originalComment = '';

      console.log(qaHistory,'message new')
      
      if (message.content && typeof message.content === 'object') {
        // For chatHistory messages
        messageId = message.id || message.content.id;
        originalComment = message.comment || '';
        
        
        // Try to find the comment in the DOM first
        const commentFromDOM = messageId && latestComments[messageId] ? latestComments[messageId] : '';
        
        return {
          id: messageId, // Store ID for debugging
          role: message.role,
          content: message?.content?.content,
          sources: message?.content?.source || [],
          timestamp: message.content.timestamp || new Date().toISOString(),
          name: message.name,
          comment: commentFromDOM || originalComment ,
          title:message.title
          // Prefer DOM comment, fall back to original,
          
        };
      } else {
        // For qaHistory messages
        messageId = message.id;
        originalComment = message.comment || '';
        
        // Try to find the comment in the DOM first
        const commentFromDOM = messageId && latestComments[messageId] ? latestComments[messageId] : '';
        
        return {
          id: messageId, // Store ID for debugging
          role: message.role,
          content: message.content,
          sources: message.sources || [],
          timestamp: message.timestamp,
          name: message.name,
          title:message.title,
          comment: commentFromDOM || originalComment // Prefer DOM comment, fall back to original
        };
      }
    });
    
    // Log the final merged history for debugging
    console.log('Merged history for PDF:', mergedHistoryForPDF.map(item => ({ id: item.id, comment: item.comment })));
    
    // Create a renderer for PDF export that exactly matches the main ContentRenderer component
    const SimpleContentRenderer = ({content }) => {
      
      // Function to process source references without using Redux
      const processSourceReferences = (text) => {
        if (!text) return text;
        
        // Regex to match source references
        const sourceRegex = /(Source:\s+)([^,]+\.pdf)(,\s+Page\s+)(\d+)/gi;
        
        // If no source references, return the original text
        if (!sourceRegex.test(text)) return text;
        
        // Split the text by the regex pattern
        const parts = text.split(sourceRegex);
        if (parts.length === 1) return text;
        
        // Reconstruct the text with the source references
        let result = '';
        for (let i = 0; i < parts.length; i++) {
          if (i % 5 === 0) {
            // This is regular text
            result += parts[i];
          } else if (i % 5 === 1) {
            // This is "Source: "
            result += parts[i];
          } else if (i % 5 === 2) {
            // This is the filename
            result += parts[i];
          } else if (i % 5 === 3) {
            // This is ", Page "
            result += parts[i];
          } else if (i % 5 === 4) {
            // This is the page number
            result += parts[i];
          }
        }
        
        return result;
      };
      
      // Process the content to ensure proper numbered list formatting
      const formattedContent = content ? content.replace(/^(\d+)[).]/gm, '$1.') : '';
      
      // Check if content contains HTML tables or markdown table syntax
      const hasHtmlTable = content && /<table[\s\S]*?<\/table>/i.test(content);
      const hasMarkdownTable = content && /\|[^|]+\|/.test(content) && content.includes('\n');
      
      // Check if content is markdown
      const isMarkdownContent = content && (
        content.includes('##') || 
        content.includes('*') || 
        content.includes('```') || 
        content.includes('|') ||
        content.includes('Source:') ||
        content.includes('- ') ||
        content.includes('1. ')
      );
      
      // Apply both source reference processing and numbered list formatting
      const processedContent = processSourceReferences(formattedContent || content);
      
      // If content has HTML tables, render it as HTML with proper styling
      if (hasHtmlTable) {
        return <div className={markdownStyles.reactMarkDown} dangerouslySetInnerHTML={{ __html: content }} />;
      } else if (isMarkdownContent || hasMarkdownTable) {
        // Use ReactMarkdown with remarkGfm to properly render tables and other markdown elements
        return (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSanitize]}
            className={markdownStyles.reactMarkDown}
            components={{
              // Override list item rendering to ensure no bold formatting
              li: ({node, ...props}) => <li style={{fontWeight: 'normal'}} {...props} />,
              // Ensure paragraphs inside list items aren't bold
              p: ({node, ...props}) => {
                // Check if this paragraph is inside a list item
                const isInListItem = node.parent && node.parent.tagName === 'li';
                return <p style={isInListItem ? {fontWeight: 'normal'} : {}} {...props} />;
              },
              // Ensure tables are properly styled
              table: ({node, ...props}) => <table style={{borderCollapse: 'collapse', border: '1px solid #e5e7eb', margin: '1rem 0'}} {...props} />,
              thead: ({node, ...props}) => <thead style={{backgroundColor: '#f9fafb'}} {...props} />,
              tbody: ({node, ...props}) => <tbody {...props} />,
              tr: ({node, ...props}) => <tr style={{borderBottom: '1px solid #e5e7eb'}} {...props} />,
              th: ({node, ...props}) => <th style={{border: '1px solid #e5e7eb', padding: '0.5rem 1rem', textAlign: 'left', fontWeight: '600'}} {...props} />,
              td: ({node, ...props}) => <td style={{border: '1px solid #e5e7eb', padding: '0.5rem 1rem'}} {...props} />
            }}
          >
            {processedContent}
          </ReactMarkdown>
        );
      }
      
      // For non-markdown content, just return the processed text
      return <div className='simple-answer'>{processedContent}</div>;
    };
    const additionalStyle = `
      body {
        font-family: Arial, sans-serif;
        overflow: visible;
        margin: 0;
        padding: 0;
      }
      .main-summary-card {
        width: 100%;
        margin: 0 auto;
        padding: 20px 40px;
        box-sizing: border-box;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
      /* Ensure consistent table styling */
      table {
        border-collapse: collapse;
        width: 100%;
        margin: 1rem 0;
      }
      th, td {
        border: 1px solid #e5e7eb;
        padding: 8px 12px;
        text-align: left;
      }
      th {
        background-color: #f9fafb;
        font-weight: 600;
      }
      /* Ensure consistent list styling */
      ul, ol {
        padding-left: 2rem;
        margin: 1rem 0;
      }
      li {
        margin-bottom: 0.5rem;
      }
      /* Ensure consistent paragraph styling */
      p {
        margin: 0.75rem 0;
        line-height: 1.5;
      }
    `;

    // Function to convert image to data URL to avoid loading delays in print window
    const convertImageToDataURL = (imageUrl) => {
      return new Promise((resolve) => {
        if (!imageUrl || imageUrl.includes('aispaceslogo.png')) {
          // For default logo, return the original URL
          resolve(imageUrl);
          return;
        }
        
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Enable CORS for external images
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/png');
            resolve(dataURL);
          } catch (error) {
            console.warn('Failed to convert image to data URL:', error);
            resolve(imageUrl); // Fallback to original URL
          }
        };
        
        img.onerror = () => {
          console.warn('Failed to load image:', imageUrl);
          resolve(imageUrl); // Fallback to original URL
        };
        
        img.src = imageUrl;
      });
    };

    // Convert logo to data URL to avoid loading delays
    convertImageToDataURL(logoUrl).then((processedLogoUrl) => {
      var printWindow = window.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>{t('analysis.pdfBrowserTitle')}</title>
            <style>${appStyle}</style>
            <style>${additionalStyle}</style>
          </head>
          <body>
            ${renderToString(
              <div className="main summary-docai-data" id="summary-docai-data">
                <div className="main-summary-card">
                  {/* Header with logo */}
                  <div  style={{marginBottom: '10px', textAlign: 'start' }}>
                    <img 
                      src={processedLogoUrl}
                      alt="Logo"
                      style={{
                        height: '60px',
                        width: 'auto',
                        maxWidth: '200px',
                        objectFit: 'contain'
                      }}
                    />
                  </div>

                {/* Title Section */}
                <div style={{
                  backgroundColor: primaryColor,
                  padding: '15px 20px',
                  marginBottom: '20px',
                  borderRadius: '4px',
                
                }}>
                  <h1 style={{
                    margin: '0',
                    fontSize: '34px',
                    fontWeight: 'bolder',
                    color: 'white'
                  }}>{chatHistoryData?.history_name} {t('analysis.pdfSummarySuffix')}</h1>
                  
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img src={constants.pdfStar} style={{ height: '25px', width: '25px'}} alt="" />
                  <p style={{
                   marginLeft: '8px',
                    fontSize: '24px',
                    color: 'white'
                  }}>{chatHistoryData?.task_name}</p>
                  </div>
                </div>
                {/* Task Info Section */}
                <div style={{
                  display: 'flex',
                  gap: '20px',
                  borderBottom: '1px solid #ddd',
                }}>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img src={constants.person} alt="" />
                    <h2>{name}</h2>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img src={constants.calendar} alt="" style={{ height: '30px', width: '30px' }} />
                    <span>{new Date().toLocaleDateString()}</span>
    
                  </div>
                </div>

                {/* Uploaded Documents Section */}
                <div style={{ marginBottom: '30px' }}>
                  <h2 style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold',
                    marginBottom: '15px',
                    color: '#333'
                  }}>{t('analysis.pdfUploadedDocsHeading')}</h2>
                  <div style={{ 
                    display: 'flex',
                     flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {userFiles?.files?.map((file, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px'
                      }}>
                        <img src={constants.file} alt="" style={{ height: '20px', width: '20px' }} />
                        <span style={{ fontSize: '14px' }}>{file.filename}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Questions & Answers Section */}
                <div style={{ marginBottom: '30px' }}>
                 
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px'
                  }}>
                    {mergedHistoryForPDF.map((chat, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {/* Question title bar - similar to UI */}
                        {chat.role === 'user' && chat.title && (
                          <div style={{
                            backgroundColor: primaryColor,
                            color: 'white',
                            padding: '5px 15px',
                            borderRadius: '0.75rem',        // mb-2
                            width: 'fit-content',          // w-fit
                            paddingRight: '2.5rem',        // pr-10
                            paddingLeft: '2.5rem', 
                            marginBottom: '-8px',
                            fontWeight: 'bold',
                            fontSize: '16px',
                          }}>
                            {chat.title}
                          </div>
                        )}
                        {/* Question author styling */}
                        <div style={{
                          fontWeight: chat.role === 'user' ? 'bold' : 'normal',
                          color: '#333',
                          fontSize: '14px'
                        }}>
                          {chat.role === 'user' ? '' : ''}
                        </div>
                        {/* Question/Answer content box */}
                        <div style={{
                          backgroundColor: chat.role === 'user' ? '#f8f9fa' : '#fff',
                          padding: '12px 15px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          lineHeight: '1.5',
                          border: '1px solid #eee',
                          marginBottom: includeComments && chat.comment ? '8px' : '0'
                        }}>
                          {/* Question/Answer content */}
                          <SimpleContentRenderer content={chat.content} />
                          {/* Sources for the question/answer if available */}
                          {chat.sources && chat.sources.length > 0 && (
                            <div style={{
                              marginTop: '8px',
                              fontSize: '12px',
                              color: '#2563eb'
                            }}>
                              {chat.sources.map((source, idx) => (
                                <div key={idx} style={{
                                  padding: '4px 8px',
                                  backgroundColor: '#f8fafc',
                                  borderRadius: '4px',
                                  marginTop: '4px'
                                }}>
                                  Source: {JSON.stringify(source)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Display comments for the question/answer if available */}
                        {includeComments && chat.comment && chat.comment.trim() !== '' && (
                          <div
                            style={{
                              pageBreakInside: 'avoid',
                              display: 'block',
                              marginTop: '8px',
                              marginBottom: '16px',
                              padding: '8px 0',
                              minHeight: '40px',
                              background: 'none',
                            }}
                          >
                            <div style={{ display: 'block', maxWidth: '100%' }}>
                              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                {t('share.commentsTitle')}
                              </div>
                              <div style={{
                                fontSize: '14px',
                                color: '#333',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-line',
                              }}>
                                {chat.comment}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
      });
  };

  const handleDescriptionSelect = (desc) => {
    setDescription(desc);
  };

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // True only when BOTH sidebars are collapsed/closed — chat becomes a fluid
  // container with 50px gutters. Otherwise restore the centered narrow column.
  const bothCollapsed = navWidth === 72 && !isSidebarOpen;

  return (
    <div
      className="fixed top-0 right-0 bottom-0 flex bg-white transition-all duration-300"
      style={{ left: `${navWidth}px` }}
    >
      {/* Main content wrapper */}
      <div className="flex flex-col flex-1 min-w-0 transition-all duration-300 overflow-hidden">
        {/* Header with breadcrumb and export */}
        <header className="flex items-center justify-between px-6 min-h-[65px] border-b border-[#E5E7EB] flex-shrink-0">
          {/* Left: Breadcrumb */}
          <div className="flex items-center gap-2 text-[14px] flex-1 min-w-0">
            <button onClick={() => navigate('/')} className="flex items-center gap-1 text-[#6B7280] hover:text-[#374151] flex-shrink-0">
              <Home size={16} />
              <span>{t('analysis.breadcrumbHome')}</span>
            </button>
            <span className="text-[#9CA3AF] flex-shrink-0">/</span>
            <button onClick={() => navigate('/history')} className="text-[#6B7280] hover:text-[#374151] flex-shrink-0">
              {t('analysis.breadcrumbProjects')}
            </button>
            {/* Only show agent and project name when data matches current historyId */}
            {chatHistoryData?.id === historyId && (
              <>
                <span className="text-[#9CA3AF] flex-shrink-0">/</span>
                {isDirectChatTask ? (
                  // Direct Chat Mode agents have no request-setup/project concept —
                  // the breadcrumb collapses to just the agent name, whether or not
                  // the session has already been auto-renamed from the first question.
                  // No fixed max-width: it only truncates ("...") when it genuinely
                  // runs out of room, and shows the full name otherwise.
                  <span className="text-[#111827] font-medium truncate min-w-0 flex-1">
                    {chatHistoryData?.task_name || t('analysis.agentFallback')}
                  </span>
                ) : (
                  <>
                    <button
                      onClick={() => navigate(`/history?agent=${chatHistoryData?.task_id}&agentName=${encodeURIComponent(chatHistoryData?.task_name || '')}`)}
                      className="text-[#6B7280] hover:text-[#374151] truncate max-w-[150px]"
                    >
                      {chatHistoryData?.task_name || t('analysis.agentFallback')}
                    </button>
                    {/* Project Detail step — clickable, returns to the project's
                        Overview/Documents/Questions/Activities tabs. The project
                        and the chat share the same record id, so this links to
                        /project-detail/<historyId>. Only render when we actually
                        have a project name to show; suppress when it's identical
                        to the chat-title leaf to avoid two identical crumbs. */}
                    {chatHistoryData?.history_name && (
                      <>
                        <span className="text-[#9CA3AF]">/</span>
                        {(() => {
                          const tid = chatHistoryData?.task_id?.toString();
                          const showDetail = tid ? overviewEnabledByTask[tid] !== false : true;
                          return showDetail ? (
                            <button
                              onClick={() => navigate(`/project-detail/${historyId}`, { state: { projectName: chatHistoryData?.history_name } })}
                              className="text-[#6B7280] hover:text-[#374151] truncate max-w-[200px]"
                              title={t('analysis.breadcrumbProjectDetail')}
                            >
                              {chatHistoryData.history_name}
                            </button>
                          ) : (
                            <span className="text-[#6B7280] truncate max-w-[200px]">
                              {chatHistoryData.history_name}
                            </span>
                          );
                        })()}
                      </>
                    )}
                    <span className="text-[#9CA3AF]">/</span>
                    <span className="text-[#111827] font-medium truncate max-w-[200px]">
                      {chatHistoryData?.history_name
                        ? t('analysis.breadcrumbChatLeaf')
                        : t('analysis.projectFallback')}
                    </span>
                  </>
                )}
              </>
            )}
          </div>

          {/* Right side: Share + Export Button + Sidebar Icon */}
          <div className="flex items-center gap-6">
            {/* Share Button */}
            <button
              onClick={() => setShowShareModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium border rounded-lg transition-colors hover:bg-[#F9FAFB]"
              style={{ borderColor: primaryColor, color: primaryColor }}
            >
              <Share2 size={15} />
              {t('share.button')}
            </button>

            {/* Export Button with Dropdown */}
            <div className="relative" ref={exportDropdownRef}>
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg text-[14px] font-medium transition-colors hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                {t('analysis.export')}
                <ChevronDown size={16} />
              </button>

              {/* Export Dropdown */}
              {showExportDropdown && (
                <div className="absolute top-full mt-2 right-0 w-56 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-50 py-1">
                  <button
                    onClick={() => {
                      exportChatsAsPDF(true);
                      setShowExportDropdown(false);
                    }}
                    className="w-full px-4 py-2.5 text-left text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                  >
                    {t('analysis.exportWithComments')}
                  </button>
                  <button
                    onClick={() => {
                      exportChatsAsPDF(false);
                      setShowExportDropdown(false);
                    }}
                    className="w-full px-4 py-2.5 text-left text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                  >
                    {t('analysis.exportWithoutComments')}
                  </button>
                </div>
              )}
            </div>

            {/* Sidebar toggle icon - only show when sidebar is closed and not pure Web-only mode */}
            {!isSidebarOpen && (!isPureWebOnly || showGeneratedFilesOnly) && (
              <button
                onClick={openSidebar}
                className="p-2 text-[#6B7280] hover:text-[#374151] hover:bg-[#F3F4F6] rounded-lg transition-colors"
                title={t('analysis.openSidebar')}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="4" width="18" height="16" rx="2" ry="2" />
                  <line x1="9" y1="4" x2="9" y2="20" />
                </svg>
              </button>
            )}
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-grow flex flex-col overflow-hidden">
          <div
            ref={chatContainerRef}
            onScroll={handleScroll}
            // Padding lives on the inner wrapper below, not here: this div is
            // the sticky-positioning scroll ancestor, and padding-top on a
            // scroll container is a fixed offset that never scrolls away —
            // any `position: sticky` child would then always sit that far
            // below the visible top edge instead of flush against it.
            className={`flex-1 overflow-y-auto thin-scrollbar ${bothCollapsed ? 'px-[50px]' : 'px-6'}`}
          >
            <div className={`${bothCollapsed ? 'w-full' : 'max-w-4xl mx-auto'} flex flex-col gap-6 py-6`} style={{ zoom: chatZoom.zoom }}>
            {
            qaLoading ? (
                <div className="flex justify-center items-center">
                  <svg className="animate-spin h-8 w-8 text-custom-yellow" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              ) : qaHistory?.map((message, index, arr) => {
                // Sidebar-predefined (Agent) question has a non-empty title on the user message.
                // For bot answers, look back to the paired user message.
                let isSidebarQuestion = false;
                let pairedTitle = '';
                if (message.title && String(message.title).trim()) {
                  isSidebarQuestion = true;
                  pairedTitle = message.title;
                } else if (message.role !== 'user') {
                  for (let i = index - 1; i >= 0; i--) {
                    if (arr[i].role === 'user') {
                      if (arr[i].title && String(arr[i].title).trim()) {
                        isSidebarQuestion = true;
                        pairedTitle = arr[i].title;
                      }
                      break;
                    }
                  }
                }
                const resolvedSqId = pairedTitle
                  ? titleToSqId[String(pairedTitle).toLowerCase().trim()] || null
                  : null;
                return (
                <ChatBlob
                  historyId={chatHistoryData?.id}
                  taskId={chatHistoryData?.task_id}
                  key={index}
                  user={message.role}
                  message={{
                    id:message.id,
                    comment: message.comment,
                    content: message.content,
                    source: message.source || message.sources || [],
                    sources: message.sources || [],
                    web_search: message.web_search || false,
                    hybrid_search: message.hybrid_search || false,
                    title: message.title || '',
                    // Images this message had attached — from the DB (File.id
                    // + filename only, no local preview) on a reload, so
                    // ChatBlob fetches the real blob to build its thumbnail.
                    attachedImages: message.attached_images || [],
                    // Same allowlist gotcha attached_images hit before — a
                    // field missing here silently vanishes even though
                    // qaHistorySlice.js's own mapping (below) always sets it.
                    restrictedFilenames: message.restricted_filenames || null,
                    generated_document_id: message.generated_document_id || null,
                    generated_document_title: message.generated_document_title || null,
                    generated_document_filename: message.generated_document_filename || null,
                    generated_document_file_size: message.generated_document_file_size || null,
                    analysis: message.analysis_meta || null
                  }}
                  openSidebar={openSidebar}
                  onImageLoad={scrollToBottom}
                  isAgentQuestion={isSidebarQuestion}
                  sqId={resolvedSqId}
                  agentQuestionTitle={pairedTitle}
                  isLastAnswer={message.id === lastBotMessageId}
                  onActivateWordMode={handleActivateWordMode}
                  onActivateDataAnalysisMode={handleActivateDataAnalysisMode}
                />
                );
              })
            }
            {chatHistory?.map((message, index, arr) => {
              // Detect sidebar-predefined (Agent) question like qaHistory does,
              // so newly-sent agent questions also get the pill + status dropdown
              // without requiring a page reload.
              let isSidebarQuestion = false;
              let pairedTitle = '';
              const ownTitle = message.title || message?.content?.title;
              if (ownTitle && String(ownTitle).trim()) {
                isSidebarQuestion = true;
                pairedTitle = ownTitle;
              } else if (message.role !== 'user') {
                for (let i = index - 1; i >= 0; i--) {
                  if (arr[i].role === 'user') {
                    const prevTitle = arr[i].title || arr[i]?.content?.title;
                    if (prevTitle && String(prevTitle).trim()) {
                      isSidebarQuestion = true;
                      pairedTitle = prevTitle;
                    }
                    break;
                  }
                }
              }
              const resolvedSqId = pairedTitle
                ? titleToSqId[String(pairedTitle).toLowerCase().trim()] || null
                : null;
              return (
              <ChatBlob
                historyId={chatHistoryData?.id}
                taskId={chatHistoryData?.task_id}
                key={index}
                user={message.role}
                message={{
                  comment: message.comment,
                  id:message.id,
                  context: message?.content?.context,
                  content: message.content,
                  source: message.source || message.sources || [],
                  sources: message.sources || [],
                  web_search: message.web_search || false,
                  hybrid_search: message.hybrid_search || false,
                  title: message.title || '',
                  failed: message.failed || false,
                  errorType: message.errorType,
                  retryPayload: message.retryPayload,
                  // Local object-URL previews (this live session's just-sent
                  // messages only — see chatSlice.js's addUserMessage).
                  attachedImages: message.attachedImages || [],
                  // Same allowlist gotcha attachedImages/attached_images hit
                  // before — a field missing here silently vanishes even
                  // though chatSlice.js's addUserMessage always sets it.
                  restrictedFilenames: message.restrictedFilenames || null,
                  // Live-session Word-generation placeholder (chatSlice.js's
                  // startWordGeneration.fulfilled) — ChatBlob polls its own
                  // status locally from this job_id.
                  wordJob: message.wordJob || null,
                  // Same allowlist gotcha as attachedImages/restrictedFilenames
                  // above — chatSlice.js's fetchChatHistory.fulfilled already
                  // sets these on reload, but they were missing here, so a
                  // finished Word result silently lost its download card the
                  // moment it left the live wordJob-polling state.
                  generated_document_id: message.generated_document_id || null,
                  generated_document_title: message.generated_document_title || null,
                  generated_document_filename: message.generated_document_filename || null,
                  generated_document_file_size: message.generated_document_file_size || null,
                  analysisJob: message.analysisJob || null,
                  analysis: message.analysis || null
                }}
                openSidebar={openSidebar}
                onRetry={(retryPayload) => retryPayload && dispatch(sendChatMessage(retryPayload))}
                onImageLoad={scrollToBottom}
                isAgentQuestion={isSidebarQuestion}
                sqId={resolvedSqId}
                agentQuestionTitle={pairedTitle}
                isLastAnswer={message.id === lastBotMessageId}
                onActivateWordMode={handleActivateWordMode}
                  onActivateDataAnalysisMode={handleActivateDataAnalysisMode}
              />
              );
            })}
              {/* An attached image is being analyzed (vision-LLM + indexing)
                  before its question can even be sent — see ChatInput.jsx's
                  waitForImagesToProcess. Shown in the chat itself, in the
                  same spot as the "AI is responding" indicator below, rather
                  than a floating toast. */}
              {analyzingImageHistoryId === historyId && (
                <div className={`flex items-center gap-3 py-4 ${bothCollapsed ? 'w-full' : 'max-w-3xl'}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M9.83705 9.63783L11.383 5H12.3317L13.8776 9.63783L18.5154 11.1838V12.1325L13.8776 13.6784L12.3317 18.3162H11.383L9.83705 13.6784L5.19922 12.1325V11.1838L9.83705 9.63783Z" fill="white" />
                    </svg>
                  </div>
                  <span className="text-[14px] text-[#374151]">{t('chatActions.analyzingImage')}</span>
                </div>
              )}
              {/* Word mode already shows its own "Creating document..." card
                  (ChatBlob's wordJob branch, present from the moment the
                  user hits send — see chatSlice.js's addPendingWordJob) —
                  suppress this generic "AI is responding" indicator then, or
                  it briefly doubles up with that specific card. */}
              {!chatHistory[chatHistory.length - 1]?.wordJob && !chatHistory[chatHistory.length - 1]?.analysisJob && (
                aiProfile?.web_search_enabled ? (
                  // Always mounted (not gated on `loading`) — it self-hides via
                  // its own `active`/draining state, so a burst of steps that
                  // arrives right as the real answer comes back still finishes
                  // displaying instead of being unmounted mid-animation.
                  <WebSearchLoadingIndicator
                    color={primaryColor}
                    wide={bothCollapsed}
                    historyId={historyId}
                    active={loading && loadingHistoryId === historyId}
                    onProgressChange={scrollToBottom}
                  />
                ) : (
                  loading && loadingHistoryId === historyId && (
                    <div className={`flex items-center gap-3 py-4 ${bothCollapsed ? 'w-full' : 'max-w-3xl'}`}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" clipRule="evenodd" d="M9.83705 9.63783L11.383 5H12.3317L13.8776 9.63783L18.5154 11.1838V12.1325L13.8776 13.6784L12.3317 18.3162H11.383L9.83705 13.6784L5.19922 12.1325V11.1838L9.83705 9.63783Z" fill="white" />
                        </svg>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor, animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor, animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor, animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  )
                )
              )}
            </div>
          </div>
          {/* Scroll to bottom button */}
          {showScrollButton && (
            <button
              onClick={scrollToBottom}
              className="absolute bottom-4 left-4 bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] text-[#6B7280] rounded-full p-2 shadow-lg transition-all duration-300 z-10"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </button>
          )}
        </main>
        <ChatZoomControls {...chatZoom} />
        {/* ChatInput at the bottom */}
        <div className={`pb-6 flex-shrink-0 ${bothCollapsed ? 'px-[50px]' : 'px-6'}`}>
          {(() => {
            const hasDisclaimer = !!(currentTask?.disclaimer_enabled && currentTask?.disclaimer_text && !disclaimerDismissed);
            const DisclaimerIcon = hasDisclaimer ? (DISCLAIMER_ICON_MAP[currentTask.disclaimer_icon] || Shield) : null;
            return (
              <div className={bothCollapsed ? 'w-full' : 'max-w-4xl mx-auto'}>
                {hasDisclaimer && (
                  <div className="mx-[25px] -mb-3">
                    <div
                      className="flex items-center gap-2 px-4 pt-2 pb-5 rounded-2xl"
                      style={{ backgroundColor: `${primaryColor}0D` }}
                    >
                      <DisclaimerIcon size={13} style={{ color: primaryColor }} className="flex-shrink-0" />
                      <span className="text-[11.5px] text-[#374151] flex-1 truncate">{currentTask.disclaimer_text}</span>
                      {currentTask.disclaimer_learn_more_content && (
                        <button
                          type="button"
                          onClick={() => setShowDisclaimerLearnMore(true)}
                          className="text-[11.5px] font-medium underline flex-shrink-0"
                          style={{ color: primaryColor }}
                        >
                          {t('agentSettings.disclaimerLearnMoreLink')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setDisclaimerDismissed(true)}
                        className="flex-shrink-0 text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                )}
                <ChatInput
                  onClick={exportChatsAsPDF}
                  hideTopPadding={hasDisclaimer}
                  historyId={chatHistoryData?.id || historyId}
                  // For a brand-new chat, ChatInput creates the history record and
                  // knows the real history_id before the /question POST fires —
                  // but without this, the URL's :historyId param (what
                  // WebSearchLoadingIndicator polls /search-status with) never
                  // updates to match, so polling never starts and the indicator
                  // freezes on its initial "understanding" state for the whole
                  // request. Mirrors the same wiring already used in Chat.jsx.
                  onHistoryCreated={(newHistoryId) => {
                    if (newHistoryId && newHistoryId !== historyId) {
                      navigate(`/analysis/${newHistoryId}`, { replace: true });
                    }
                  }}
                  taskName={chatHistoryData?.task_name}
                  project={chatHistoryData?.history_name}
                  taskID={chatHistoryData?.task_id || taskID}
                  searchType={undefined}
                  prompt={description}
                  activateWordModeSignal={wordModeActivationSignal}
                  activateAnalysisModeSignal={analysisModeActivationSignal}
                  toggleSearchSection={toggleSearchSection}
                  allowLocalFiles={(() => {
                    const tid = chatHistoryData?.task_id?.toString();
                    if (!tid) return true;
                    return showFilesTabByTask[tid] !== false && allowLocalFilesByTask[tid] !== false;
                  })()}
                />
              </div>
            );
          })()}
        </div>
        {showDisclaimerLearnMore && currentTask?.disclaimer_learn_more_content && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowDisclaimerLearnMore(false)} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[520px] mx-4 max-h-[90vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
                <h2 className="text-[18px] font-bold text-[#111827]">{t('agentSettings.disclaimerModalTitle')}</h2>
                <button
                  onClick={() => setShowDisclaimerLearnMore(false)}
                  className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="px-6 pb-6 flex-1 overflow-y-auto text-[14px] text-[#374151] whitespace-pre-wrap">
                {currentTask.disclaimer_learn_more_content}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right Sidebar — hidden entirely only in pure Web-only mode; "Documents + Web"
          keeps it so users can still browse/upload documents for the RAG half. */}
      <div
        className={`h-full flex-shrink-0 transition-all duration-300 overflow-hidden bg-white ${(!isPureWebOnly || showGeneratedFilesOnly) && isSidebarOpen ? 'border-l border-[#E5E7EB]' : 'hidden'}`}
        style={{ width: (!isPureWebOnly || showGeneratedFilesOnly) && isSidebarOpen ? `${sidebarWidth}px` : '0' }}
      >
        <Sidebar
          taskID={chatHistoryData?.id === historyId ? chatHistoryData?.task_id : null}
          taskName={chatHistoryData?.id === historyId ? chatHistoryData?.task_name : null}
          historyId={historyId}
          onClose={closeSidebar}
          isOpen={isSidebarOpen}
          onWidthChange={handleWidthChange}
          onDescriptionSelect={handleDescriptionSelect}
          // ChatGPT-style: an Instruction/Standard Question can't be sent
          // as a new message while this chat's previous response hasn't
          // settled yet — same rule ChatInput.jsx enforces for typed
          // messages (its own isResponsePending is this exact expression).
          questionsSendDisabled={loading && loadingHistoryId === historyId}
          activeTab={activeTab}
          questionsEnabled={(() => {
            if (isPureWebOnly) return false;
            const tid = chatHistoryData?.task_id?.toString();
            if (!tid) return true;
            return showProjectDetailByTask[tid] !== false;
          })()}
          filesEnabled={(() => {
            if (isPureWebOnly) return false;
            const tid = chatHistoryData?.task_id?.toString();
            if (!tid) return true;
            return showFilesTabByTask[tid] !== false;
          })()}
          allowLocalFiles={(() => {
            const tid = chatHistoryData?.task_id?.toString();
            if (!tid) return true;
            return allowLocalFilesByTask[tid] !== false;
          })()}
          generatedFilesOnly={showGeneratedFilesOnly}
        />
      </div>
      <ToastContainer />
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        historyId={historyId}
        projectName={chatHistoryData?.history_name || chatHistoryData?.task_name || ''}
        currentUser={{
          name: name || localStorage.getItem('email'),
          email: localStorage.getItem('email'),
        }}
      />
    </div>
  )
}

export default Analysis
